import { useQuery } from "@tanstack/react-query";
import { Application } from "shared/types";
import api from "api/landlord/address";
import { useState } from "react";
import { DecisionAction } from "shared/types/application";
import { Template } from "types/landlord/verificationTemplate";
import { FlowConfig, Views, FormData } from "./types";
import { UnlistUnitTiming } from "api/landlord/templates";
import { RuleSetDecision } from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/types";
import { Reasons } from "types/application";
import { conditionKeysMap } from "./constants";

const flows: FlowConfig = {
  decline: [
    Views.MakeDecision,
    Views.Conditions,
    Views.DecisionSettings,
    Views.Email,
    Views.Success
  ],
  conditional_approve: [
    Views.MakeDecision,
    Views.Conditions,
    Views.DecisionSettings,
    Views.Email,
    Views.Success
  ],
  approve: [
    Views.MakeDecision,
    Views.DecisionSettings,
    Views.Email,
    Views.Success
  ]
};

export const useMakeDecisionController = (applicationId: string) => {
  const [view, setView] = useState<Views>(Views.MakeDecision);
  const [formData, setFormData] = useState<FormData>({
    decisionNotes: "",
    unlistUnit: true,
    sendNotifications: true,
    addApplicantsToPMS: false,
    conditions: [],
    additionalCondition: "",
    additionalEmailText: ""
  });

  const flow = flows[formData.decision || DecisionAction.Approve].filter(
    (v) => !(v === Views.Email && !formData.sendNotifications)
  );

  const setNextView = () => {
    const currentViewIndex = flow.indexOf(view);
    if (currentViewIndex == 0)
      resetConditions(
        applicationMeta.data?.rule_reasons,
        applicationMeta.data?.recommendation
      );
    setView(flow[currentViewIndex + 1]);
  };

  const setPrevView = () => {
    const currentViewIndex = flow.indexOf(view);
    setView(flow[currentViewIndex - 1]);
  };

  const getTransformedConditions = (
    ruleReasons: Reasons[],
    recommendation: DecisionAction | undefined
  ) => {
    if (applicationMeta.data?.recommendation !== formData.decision) return [];

    const ruleSetType =
      recommendation === DecisionAction.ConditionalApprove
        ? RuleSetDecision.Approve
        : RuleSetDecision.Decline;

    return ruleReasons.reduce<string[]>((result, ruleSet) => {
      if (ruleSet.rule_set_type !== ruleSetType) return result;

      return result.concat(
        ruleSet.rules.reduce<string[]>((fields, rule) => {
          if (
            rule.result ===
            (recommendation !== DecisionAction.ConditionalApprove)
          ) {
            const condition = conditionKeysMap[rule.field_name];
            if (condition) fields.push(condition);
          }

          return fields;
        }, [])
      );
    }, []);
  };

  const resetConditions = (
    ruleReasons: Reasons[] | undefined,
    recommendation: DecisionAction | undefined
  ) => {
    if (ruleReasons)
      setFormData((prev) => ({
        ...prev,
        conditions: getTransformedConditions(ruleReasons, recommendation)
      }));
  };

  const applicationMeta = useQuery<
    Application & {
      verification_template: Template;
      score: number;
      rule_reasons?: Reasons[];
    }
  >({
    queryKey: ["APPLICATION", applicationId],
    queryFn: () =>
      api.getApplicationById(applicationId || "").then(
        (
          res: Application & {
            verification_template: Template;
            score: number;
            rule_reasons?: Reasons[];
          }
        ) => {
          setFormData((prev) => ({
            ...prev,
            conditions: res.rule_reasons
              ? getTransformedConditions(res.rule_reasons, res.recommendation)
              : [],
            decision: res.recommendation as DecisionAction,
            unlistUnit:
              res.verification_template.automatically_unlist_unit_timing ===
              UnlistUnitTiming.ON_APPROVE,
            sendNotifications:
              res.verification_template.notify_applicants ||
              prev.sendNotifications,
            addApplicantsToPMS: res.property?.pms_id
              ? res.verification_template[
                  `add_applicants_to_pms_on_${res.recommendation}`
                ] || prev.addApplicantsToPMS
              : false
          }));
          return res;
        }
      ),
    enabled: !!applicationId
  });

  return {
    applicationMeta,
    formData,
    setFormData,
    setNextView,
    setPrevView,
    view,
    flow,
    resetConditions
  };
};
